/**
 * Collapsible config editor: name, filter rule builder (and/or, one nested
 * group level), column picker (add/remove/reorder), sort picker, page size.
 * Every change flows up through onChange; the table screen debounces the
 * JSON save into the .base file's Y.Text.
 */
import { useState } from 'react';
import {
  CMPS,
  CMP_LABELS,
  FILE_PROPS,
  TAGS_PROP,
  isGroup,
  propLabel,
  type BaseConfig,
  type Cmp,
  type FilterGroup,
  type FilterRule,
  type SortKey,
} from './config.js';

export interface ConfigEditorProps {
  config: BaseConfig;
  /** Frontmatter keys discovered across all notes (no file.* / tags). */
  fmProps: string[];
  onChange: (next: BaseConfig) => void;
}

const NO_VALUE: Cmp[] = ['exists', 'missing'];

/** All selectable props: file pseudo-props, tags, then discovered fm keys. */
function allProps(fmProps: string[], extra: string[] = []): string[] {
  const seen = new Set<string>([...FILE_PROPS, TAGS_PROP]);
  const out = [...FILE_PROPS, TAGS_PROP];
  for (const p of [...fmProps, ...extra]) {
    if (!seen.has(p)) {
      seen.add(p);
      out.push(p);
    }
  }
  return out;
}

function PropSelect({
  value,
  options,
  onChange,
}: {
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  const opts = options.includes(value) || value === '' ? options : [value, ...options];
  return (
    <select
      className="base-select"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {value === '' && <option value="">property…</option>}
      {opts.map((p) => (
        <option key={p} value={p}>
          {propLabel(p)}
        </option>
      ))}
    </select>
  );
}

// ------------------------------------------------------------------ filters

function RuleRow({
  rule,
  options,
  onChange,
  onRemove,
}: {
  rule: FilterRule;
  options: string[];
  onChange: (r: FilterRule) => void;
  onRemove: () => void;
}) {
  return (
    <div className="base-rule">
      <PropSelect value={rule.prop} options={options} onChange={(prop) => onChange({ ...rule, prop })} />
      <select
        className="base-select base-select-cmp"
        value={rule.cmp}
        onChange={(e) => onChange({ ...rule, cmp: e.target.value as Cmp })}
      >
        {CMPS.map((c) => (
          <option key={c} value={c}>
            {CMP_LABELS[c]}
          </option>
        ))}
      </select>
      {!NO_VALUE.includes(rule.cmp) && (
        <input
          className="base-input base-rule-value"
          value={rule.value ?? ''}
          placeholder="value"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          onChange={(e) => onChange({ ...rule, value: e.target.value })}
        />
      )}
      <button type="button" className="base-mini-btn danger" title="Remove rule" onClick={onRemove}>
        ×
      </button>
    </div>
  );
}

function OpToggle({ op, onChange }: { op: 'and' | 'or'; onChange: (op: 'and' | 'or') => void }) {
  return (
    <span className="base-op-toggle">
      <button
        type="button"
        className={op === 'and' ? 'active' : ''}
        onClick={() => onChange('and')}
      >
        All
      </button>
      <button type="button" className={op === 'or' ? 'active' : ''} onClick={() => onChange('or')}>
        Any
      </button>
    </span>
  );
}

function GroupEditor({
  group,
  options,
  depth,
  onChange,
  onRemove,
}: {
  group: FilterGroup;
  options: string[];
  depth: number;
  onChange: (g: FilterGroup) => void;
  onRemove?: () => void;
}) {
  const setRule = (i: number, r: FilterRule | FilterGroup) =>
    onChange({ ...group, rules: group.rules.map((x, j) => (j === i ? r : x)) });
  const removeRule = (i: number) =>
    onChange({ ...group, rules: group.rules.filter((_, j) => j !== i) });

  return (
    <div className={`base-group${depth > 0 ? ' nested' : ''}`}>
      <div className="base-group-head">
        <OpToggle op={group.op} onChange={(op) => onChange({ ...group, op })} />
        <span className="base-group-label muted">of the following match</span>
        <span style={{ flex: 1 }} />
        {onRemove && (
          <button type="button" className="base-mini-btn danger" title="Remove group" onClick={onRemove}>
            ×
          </button>
        )}
      </div>
      {group.rules.map((r, i) =>
        isGroup(r) ? (
          <GroupEditor
            key={i}
            group={r}
            options={options}
            depth={depth + 1}
            onChange={(g) => setRule(i, g)}
            onRemove={() => removeRule(i)}
          />
        ) : (
          <RuleRow
            key={i}
            rule={r}
            options={options}
            onChange={(nr) => setRule(i, nr)}
            onRemove={() => removeRule(i)}
          />
        ),
      )}
      <div className="base-group-actions">
        <button
          type="button"
          className="base-mini-btn add"
          onClick={() =>
            onChange({ ...group, rules: [...group.rules, { prop: '', cmp: 'contains', value: '' }] })
          }
        >
          ＋ Rule
        </button>
        {depth === 0 && (
          <button
            type="button"
            className="base-mini-btn add"
            onClick={() =>
              onChange({
                ...group,
                rules: [...group.rules, { op: 'or', rules: [{ prop: '', cmp: 'contains', value: '' }] }],
              })
            }
          >
            ＋ Group
          </button>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ editor

export function ConfigEditor({ config, fmProps, onChange }: ConfigEditorProps) {
  const [addCol, setAddCol] = useState('');
  const columnProps = config.columns.map((c) => c.prop);
  const options = allProps(fmProps, [
    ...columnProps,
    ...config.sort.map((s) => s.prop),
  ]);
  const addable = options.filter((p) => !columnProps.includes(p));

  const setColumns = (columns: BaseConfig['columns']) => onChange({ ...config, columns });
  const setSort = (sort: SortKey[]) => onChange({ ...config, sort });

  const moveColumn = (i: number, delta: number) => {
    const j = i + delta;
    if (j < 0 || j >= config.columns.length) return;
    const cols = [...config.columns];
    const [c] = cols.splice(i, 1);
    cols.splice(j, 0, c);
    setColumns(cols);
  };

  return (
    <div className="base-config">
      <section className="base-config-section">
        <label className="base-config-title" htmlFor="base-name-input">
          Name
        </label>
        <input
          id="base-name-input"
          className="base-input"
          value={config.name}
          onChange={(e) => onChange({ ...config, name: e.target.value })}
        />
      </section>

      <section className="base-config-section">
        <div className="base-config-title">Filters</div>
        <GroupEditor
          group={config.filters}
          options={options}
          depth={0}
          onChange={(filters) => onChange({ ...config, filters })}
        />
      </section>

      <section className="base-config-section">
        <div className="base-config-title">Columns</div>
        {config.columns.map((c, i) => (
          <div className="base-col-row" key={`${c.prop}:${i}`}>
            <span className="base-col-name">{propLabel(c.prop)}</span>
            <button
              type="button"
              className="base-mini-btn"
              title="Move up"
              disabled={i === 0}
              onClick={() => moveColumn(i, -1)}
            >
              ↑
            </button>
            <button
              type="button"
              className="base-mini-btn"
              title="Move down"
              disabled={i === config.columns.length - 1}
              onClick={() => moveColumn(i, 1)}
            >
              ↓
            </button>
            <button
              type="button"
              className="base-mini-btn danger"
              title="Remove column"
              disabled={config.columns.length <= 1}
              onClick={() => setColumns(config.columns.filter((_, j) => j !== i))}
            >
              ×
            </button>
          </div>
        ))}
        <div className="base-col-add">
          <select
            className="base-select"
            value={addCol}
            onChange={(e) => setAddCol(e.target.value)}
          >
            <option value="">add column…</option>
            {addable.map((p) => (
              <option key={p} value={p}>
                {propLabel(p)}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="base-mini-btn add"
            disabled={!addCol}
            onClick={() => {
              if (!addCol) return;
              setColumns([...config.columns, { prop: addCol }]);
              setAddCol('');
            }}
          >
            ＋ Add
          </button>
        </div>
      </section>

      <section className="base-config-section">
        <div className="base-config-title">Sort</div>
        {config.sort.map((s, i) => (
          <div className="base-sort-row" key={i}>
            <PropSelect
              value={s.prop}
              options={options}
              onChange={(prop) => setSort(config.sort.map((x, j) => (j === i ? { ...x, prop } : x)))}
            />
            <button
              type="button"
              className="base-mini-btn wide"
              title="Toggle direction"
              onClick={() =>
                setSort(
                  config.sort.map((x, j) =>
                    j === i ? { ...x, dir: x.dir === 'asc' ? 'desc' : 'asc' } : x,
                  ),
                )
              }
            >
              {s.dir === 'asc' ? 'A→Z' : 'Z→A'}
            </button>
            <button
              type="button"
              className="base-mini-btn danger"
              title="Remove sort key"
              onClick={() => setSort(config.sort.filter((_, j) => j !== i))}
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          className="base-mini-btn add"
          onClick={() => setSort([...config.sort, { prop: 'file.name', dir: 'asc' }])}
        >
          ＋ Sort key
        </button>
      </section>

      <section className="base-config-section">
        <label className="base-config-title" htmlFor="base-pagesize">
          Rows per page
        </label>
        <select
          id="base-pagesize"
          className="base-select"
          value={String(config.pageSize ?? 50)}
          onChange={(e) => onChange({ ...config, pageSize: Number(e.target.value) })}
        >
          {[25, 50, 100, 250].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </section>
    </div>
  );
}
